import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { QuickBooksRule, deleteQuickBooksRule } from 'api/quickBooks';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface DeleteRuleDialogProps extends AlertDialog.AlertDialogRootProps {
    rule: QuickBooksRule;
}

const DeleteRuleDialog = ({ rule, children, ...props }: DeleteRuleDialogProps) => {
    const { accountId } = useCurrentAccount();
    const { error, success } = useToast();

    const { isPending: isDeleting, mutate: deleteRule } = useMutation({
        mutationFn: ({ ruleId }: { ruleId: number }) => {
            return deleteQuickBooksRule(accountId, ruleId);
        },
        onSuccess: () => {
            success('QuickBooks rule deleted!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.quickBooksRules(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unknown error');
        },
    });

    return (
        <AlertDialog.Root type={'error'} {...props}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete Custom Rule</AlertDialog.Title>
                        <AlertDialog.Description>
                            Custom rule "{rule.name}" will immediately be deleted. You will not be
                            able to undo this action.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() =>
                                deleteRule({
                                    ruleId: rule.id,
                                })
                            }
                            disabled={isDeleting}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DeleteRuleDialog;
const DeleteRuleDialogTrigger = AlertDialog.Trigger;
const Trigger = DeleteRuleDialogTrigger;

export {
    Root,
    Trigger,
    //
    DeleteRuleDialog,
    DeleteRuleDialogTrigger,
};
